import { createContext, useContext, useState } from 'react'
import type { ReactNode } from 'react'

// Primitivas compartidas mínimas. Los módulos agregan las suyas en su propia carpeta;
// aquí solo vive lo que TODOS usan.

// ── Fotos del roster, disponibles para CUALQUIER Avatar ──
// Había ~35 usos de Avatar y solo UNO le pasaba `photo`, así que casi toda la app
// mostraba iniciales aunque /api/team ya devuelve photoUrl. En vez de tocar los 35
// call sites, el roster se lee UNA vez y Avatar lo resuelve solo por correo.
// Las claves se normalizan a minúsculas: los correos vienen con capitalización
// inconsistente según de dónde salgan (miembros del proyecto, responsables, chat).
// El PROVEEDOR vive en photos.tsx, no aquí: cargarlo necesita apiGet, que arrastra
// firebase/auth, y este archivo lo importan tests que corren fuera del navegador.
export const PhotosCtx = createContext<Map<string, string | null> | null>(null)

const PALETTE = ['#3fa7ff', '#2ecc71', '#ff5a4d', '#ffcb05', '#b58cff', '#ff9e40', '#4dd0e1']

export function initialsOf(nameOrEmail: string): string {
  const base = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0]! : nameOrEmail
  const parts = base.split(/[\s._-]+/).filter(Boolean)
  const a = parts[0]?.[0] ?? '?'
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '')
  return (a + b).toUpperCase()
}

export function colorFor(key: string): string {
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return PALETTE[h % PALETTE.length]!
}

// `photo` es la foto de Google del roster (/api/team la expone en photoUrl). Si no
// hay o falla la carga, caen las iniciales con color derivado del nombre — nunca
// se queda un hueco.
// Si NO se pasa `photo` (undefined), se busca sola en el roster usando el correo:
// primero `title` (el call site suele poner ahí el correo cuando `who` es el nombre)
// y si no, `who`. Pasar `photo={null}` EXPLÍCITAMENTE desactiva la búsqueda — así un
// call site que ya sabe que no hay foto no dispara el fallback.
export function Avatar({
  who,
  size = 26,
  title,
  photo,
}: {
  who: string
  size?: number
  title?: string
  photo?: string | null
}) {
  const [rota, setRota] = useState(false)
  const fotos = useContext(PhotosCtx)
  let resuelta = photo
  if (photo === undefined && fotos) {
    const porTitle = title?.includes('@') ? fotos.get(title.toLowerCase()) : undefined
    const porWho = who.includes('@') ? fotos.get(who.toLowerCase()) : undefined
    resuelta = porTitle ?? porWho ?? null
  }
  const usarFoto = !!resuelta && !rota
  return (
    <span
      className={usarFoto ? 'av has-photo' : 'av'}
      title={title ?? who}
      style={{ width: size, height: size, ['--av-c' as string]: colorFor(who) }}
    >
      {usarFoto ? (
        // referrerPolicy no-referrer: las URLs de foto de Google devuelven 403 si
        // se manda referer, y el avatar caería a un roto silencioso.
        <img src={resuelta!} alt="" referrerPolicy="no-referrer" onError={() => setRota(true)} />
      ) : (
        initialsOf(who)
      )}
    </span>
  )
}

export function AvatarCluster({
  emails,
  max = 4,
  photos,
}: {
  emails: string[]
  max?: number
  // Mapa opcional clave→foto. La clave es la MISMA que se pasa en `emails`
  // (unas veces correo, otras nombre, según el call site).
  photos?: Map<string, string | null>
  }) {
  const shown = emails.slice(0, max)
  const rest = emails.length - shown.length
  return (
    <span className="av-cluster">
      {shown.map((e) => (
        <Avatar key={e} who={e} photo={photos?.get(e)} />
      ))}
      {rest > 0 && <span className="av av-more">+{rest}</span>}
    </span>
  )
}

/**
 * La ✕ de cerrar/quitar, como SVG inline de trazo 1.7 (regla de la casa: nunca un
 * glifo de texto como icono). Vivía suelta como el carácter "✕" en once pantallas:
 * se pinta con la fuente del sistema, cambia de peso y de alineación óptica en cada
 * plataforma y queda descentrada dentro del .icon-btn v2, que dimensiona `svg` y no
 * texto. Un solo componente para que la misma acción se vea igual en toda la app.
 */
export function IconoCerrar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <p className="empty-title">{title}</p>
      {hint && <p className="muted">{hint}</p>}
      {action}
    </div>
  )
}

export function StatusPill({ label, color }: { label: string; color: string }) {
  return (
    <span className="status-pill" style={{ ['--c' as string]: color }}>
      {label}
    </span>
  )
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

// "Hoy" como fecha de negocio LOCAL (YYYY-MM-DD). new Date().toISOString() es UTC:
// después de las 18:00 en MX ya es "mañana" y todo se marca vencido — usar esto.
export function todayLocal(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// Fecha corta legible en es-MX. OJO: las fechas de negocio YYYY-MM-DD se parsean
// por PARTES (new Date('YYYY-MM-DD') es medianoche UTC → en MX se pinta un día antes).
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  if (DATE_ONLY.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y!, m! - 1, d!).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
  }
  return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-MX', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'ahora'
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`
  return `hace ${Math.floor(s / 86400)} d`
}
